import React, { useCallback } from 'react';
import {
  Form,
  RenderFuncProps,
  SelectField,
  useNotification,
} from '@dispatch/web/ui';
import { TaskCancelRequest, TaskInfo, TaskType } from '@dispatch/shared/types';
import { Grid } from '@mui/material';
import {
  BulkTaskActionDrawer,
  formCancelDefaults,
} from '@dispatch/web/features/tasks';
import { useNavigate } from 'react-router-dom';
import {
  useBulkCancelTasks,
  useCompany,
  useCurrentUserResource,
  usePermissions,
} from '@dispatch/shared/hooks';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

const taskCancelValidationSchema = yup.object().shape({
  cancelReason: yup.string().notRequired(),
});

export const BulkTaskCancellation = () => {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { isAdjuster } = useCurrentUserResource();
  const { cancelReasons, cancelReasonsStatus } = useCompany();
  const { notifySuccess, notifyError } = useNotification();

  const {
    control: formCancelControl,
    formState: formCancelState,
    handleSubmit: handleCancelSubmit,
    reset: resetCancel,
  } = useForm({
    defaultValues: { ...formCancelDefaults },
    resolver: yupResolver(taskCancelValidationSchema),
  });

  const { mutate: bulkCancel, status: bulkCancelStatus } = useBulkCancelTasks(
    () => {
      notifySuccess('', 'Tasks cancelled successfully');
      navigate('/tasks');
    },
    () => notifyError('Error', 'Tasks not cancelled')
  );

  const filterCancellableRow = useCallback(
    (row: TaskInfo): boolean => {
      if (!permissions) return false;

      if (row.targetQueue === 'egress' || row.targetQueue === 'cancelled_q') {
        return false;
      }

      const taskType = row.taskType;
      const hasClaimOwnerPermission = permissions.can('cancel', 'claim-owner');
      const hasComplexPermission = permissions.can('upsert', 'complex-loss');
      const hasOtherTaskEditPermission = permissions.can('update', 'task');

      if (isAdjuster) {
        if (taskType === TaskType.CLAIM_OWNER) {
          return hasClaimOwnerPermission;
        }
        return true;
      } else {
        if (hasOtherTaskEditPermission) {
          if (
            taskType === TaskType.CLAIM_HANDLER ||
            taskType === TaskType.NON_COMPLEX ||
            !taskType
          )
            return true;
          if (hasClaimOwnerPermission && taskType === TaskType.CLAIM_OWNER)
            return true;
          if (
            hasComplexPermission &&
            (taskType === TaskType.COMPLEX ||
              taskType === TaskType.COMPLEX_CHILD)
          )
            return true;
        }

        if (
          (hasClaimOwnerPermission && taskType === TaskType.CLAIM_OWNER) ||
          (hasComplexPermission &&
            (taskType === TaskType.COMPLEX ||
              taskType === TaskType.COMPLEX_CHILD))
        ) {
          return true;
        }
      }
      return false;
    },
    [permissions, isAdjuster]
  );

  const renderCancelContent = (selectedCount: number): React.ReactElement => (
    <Form<TaskCancelRequest>
      errors={formCancelState.errors}
      control={formCancelControl}
      handleSubmit={handleCancelSubmit}
      render={({ control, errors }: RenderFuncProps) => {
        return (
          <>
            <span style={{ display: 'block', marginBottom: 8 }}>
              Do you really want to <b>Cancel {selectedCount} task(s)</b>?
            </span>
            <Grid container spacing={1} alignItems="baseline">
              <Grid size={8}>
                {cancelReasonsStatus === 'error' && (
                  <div style={{ marginBottom: 8, color: '#d32f2f' }}>
                    Failed to load cancel reasons
                  </div>
                )}
                <SelectField
                  id="cancelReason"
                  name="cancelReason"
                  label="Reason"
                  control={control}
                  errors={errors}
                  options={cancelReasons || []}
                />
              </Grid>
            </Grid>
          </>
        );
      }}
    />
  );

  const handleConfirmCancel = (selectedRows: TaskInfo[]) => {
    handleCancelSubmit(({ cancelReason }: { cancelReason?: string }) => {
      const taskIds = selectedRows.map((row) => row.id);
      bulkCancel({ taskIds, reason: cancelReason, status: 'cancelled' });
    })();
  };

  return (
    <BulkTaskActionDrawer
      title="Cancel Tasks"
      actionButton={{
        label: 'Cancel Tasks',
        color: 'error',
        testId: 'cancel-task-button',
      }}
      modalType="bulkCancelTask"
      noRowsMessage="No Tasks for cancelling in bulk."
      filterRow={filterCancellableRow}
      renderConfirm={renderCancelContent}
      confirmTitle="Cancel Tasks"
      actionStatus={bulkCancelStatus}
      onConfirm={handleConfirmCancel}
      onModalDeny={resetCancel}
    />
  );
};
